import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { catalogAPI } from '../../utils/api';
import {
  Eye, EyeOff, RefreshCw, ExternalLink, Search, Filter, Download,
  ChevronDown, X, AlertTriangle, Clock, TrendingUp, Percent, Columns3,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => n != null && n > 0 ? `₹${Number(n).toLocaleString('en-IN')}` : null;
const fmtCompact = (n) => {
  if (!n || n <= 0) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString('en-IN')}`;
};
const daysSince = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? 'today' : d === 1 ? '1d ago' : `${d}d ago`;
};

const COL_GROUPS = {
  primary: { label: 'Primary', default: true },
  baseline: { label: 'Internal Baseline', default: false },
  website: { label: 'Website Controls', default: false },
  audit: { label: 'Audit/Meta', default: false },
};

export default function VoltideSKUSheet() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [drawerRow, setDrawerRow] = useState(null);
  const [search, setSearch] = useState('');
  const [colGroups, setColGroups] = useState({ primary: true, baseline: false, website: false, audit: false });
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ published: '', compat: '' });
  const [bulkMargin, setBulkMargin] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogAPI.getSheet();
      setRows(res.data);
    } catch { toast.error('Failed to load sheet'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await catalogAPI.syncSheet();
      const d = res.data;
      toast.success(`Synced: ${d.created} new, ${d.updated} updated, ${d.skipped} unchanged`);
      fetchData();
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  // Filter + search
  const filtered = useMemo(() => {
    let list = rows;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(r =>
        (r.voltide_sku_code || '').toLowerCase().includes(s) ||
        (r.public_product_name || '').toLowerCase().includes(s) ||
        (r.vendor_sku_code || '').toLowerCase().includes(s) ||
        (r.pcs_sku || '').toLowerCase().includes(s) ||
        (r.battery_sku || '').toLowerCase().includes(s)
      );
    }
    if (filters.published === 'published') list = list.filter(r => r.publish_to_website);
    if (filters.published === 'hidden') list = list.filter(r => !r.publish_to_website);
    if (filters.compat) list = list.filter(r => r.compatibility_status === filters.compat);
    if (sortField) {
      list = [...list].sort((a, b) => {
        const va = a[sortField] ?? 0, vb = b[sortField] ?? 0;
        return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
      });
    }
    return list;
  }, [rows, search, filters, sortField, sortDir]);

  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.sheet_row_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.sheet_row_id)));
  };
  const toggleOne = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Bulk actions
  const doBulk = async (action, value = null) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkLoading(true);
    try {
      const res = await catalogAPI.bulkSheetAction({ action, row_ids: ids, value });
      toast.success(`${action}: ${res.data.computed || res.data.count || 'done'}`);
      setSelected(new Set());
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || `Bulk ${action} failed`); }
    finally { setBulkLoading(false); }
  };

  const handleExport = () => {
    const exportRows = filtered.filter(r => selected.size === 0 || selected.has(r.sheet_row_id));
    const csv = [
      'voltide_sku_code,vendor_sku,kW,kWh,total_cost,margin_pct,buy_from_ex_gst,rent_per_kwh_mo,baseline_kwh,min_billable_kwh,published',
      ...exportRows.map(r => [
        r.voltide_sku_code, `"${r.vendor_sku_code}"`, r.rated_power_kw, r.battery_energy_kwh,
        r.total_bess_cost || '', (r.margin_pct * 100).toFixed(1), r.buy_from_price_ex_gst || '',
        r.rent_from_inr_per_kwh_month || '', r.baseline_monthly_units_kwh, r.min_billable_units_kwh,
        r.publish_to_website
      ].join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'voltide_sku_sheet.csv';
    a.click();
    toast.success(`Exported ${exportRows.length} row(s)`);
  };

  const toggleColGroup = (g) => setColGroups(prev => ({ ...prev, [g]: !prev[g] }));
  const hasFilters = filters.published || filters.compat;

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" /></div>;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input placeholder="Search SKU, PCS, battery..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="sheet-search" />
        </div>
        {/* Column group toggles */}
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-sm p-0.5">
          {Object.entries(COL_GROUPS).map(([k, { label }]) => (
            <button key={k} onClick={() => toggleColGroup(k)} data-testid={`col-toggle-${k}`}
              className={`px-2.5 py-1 text-[10px] font-medium rounded-sm transition-colors ${colGroups[k] ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="h-9 text-xs" onClick={() => setShowFilters(!showFilters)} data-testid="sheet-filter-btn">
          <Filter className="w-3.5 h-3.5 mr-1" /> Filter
          {hasFilters && <span className="ml-1 bg-blue-100 text-blue-700 rounded-full px-1.5 text-[10px]">!</span>}
        </Button>
        {hasFilters && (
          <button onClick={() => setFilters({ published: '', compat: '' })} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-0.5">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-400">{filtered.length} of {rows.length}</span>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button size="sm" className="bg-slate-900 h-8 text-xs" onClick={handleSync} disabled={syncing} data-testid="sync-sheet-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Cost Matrix'}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="flex items-center gap-3 mb-3 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="sheet-filters">
          <Select value={filters.published || '__all__'} onValueChange={v => setFilters(p => ({ ...p, published: v === '__all__' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs w-36"><SelectValue placeholder="Publish status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.compat || '__all__'} onValueChange={v => setFilters(p => ({ ...p, compat: v === '__all__' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs w-48"><SelectValue placeholder="Compatibility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="YES">YES</SelectItem>
              <SelectItem value="POSSIBLE">POSSIBLE</SelectItem>
              <SelectItem value="ENGINEERING_REVIEW">ENGINEERING_REVIEW</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 mb-3 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="bulk-bar">
          <span className="text-xs font-medium text-blue-700">{selected.size} selected</span>
          <div className="w-px h-4 bg-blue-200" />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doBulk('publish')} disabled={bulkLoading}>
            <Eye className="w-3 h-3 mr-1" /> Publish
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doBulk('unpublish')} disabled={bulkLoading}>
            <EyeOff className="w-3 h-3 mr-1" /> Unpublish
          </Button>
          <div className="flex items-center gap-1">
            <Input type="number" step="1" className="h-7 text-xs w-16" placeholder="%" value={bulkMargin} onChange={e => setBulkMargin(e.target.value)} data-testid="bulk-margin-input" />
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { if (bulkMargin) doBulk('set_margin', parseFloat(bulkMargin) / 100); }} disabled={bulkLoading || !bulkMargin}>
              <Percent className="w-3 h-3 mr-1" /> Margin
            </Button>
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doBulk('recompute_rent')} disabled={bulkLoading}>
            <RefreshCw className="w-3 h-3 mr-1" /> Recompute
          </Button>
        </div>
      )}

      {/* TABLE */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-x-auto">
        <table className="text-sm" data-testid="voltide-sheet-table" style={{ minWidth: colGroups.baseline || colGroups.website || colGroups.audit ? '1600px' : '1280px' }}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-500 uppercase">
              <th className="w-[40px] px-2 py-3 sticky left-0 z-20 bg-slate-50"><Checkbox checked={allSelected} onCheckedChange={toggleAll} /></th>
              <th className="w-[200px] px-3 py-3 text-left sticky left-[40px] z-20 bg-slate-50 cursor-pointer" onClick={() => handleSort('voltide_sku_code')}>
                <span className="flex items-center gap-1">Voltide SKU <ArrowUpDown className="w-3 h-3" /></span>
              </th>
              <th className="w-[180px] px-3 py-3 text-left sticky left-[240px] z-20 bg-slate-50">Vendor SKU <span className="text-[8px] text-amber-500">(internal)</span></th>
              <th className="w-[240px] px-3 py-3 text-left sticky left-[420px] z-20 bg-slate-50">Name / Config</th>
              {colGroups.primary && (<>
                <th className="w-[120px] px-3 py-3 text-right cursor-pointer" onClick={() => handleSort('total_bess_cost')}>
                  <span className="flex items-center justify-end gap-1">Total Cost <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="w-[80px] px-3 py-3 text-center">Margin %</th>
                <th className="w-[140px] px-3 py-3 text-right cursor-pointer" onClick={() => handleSort('buy_from_price_ex_gst')}>
                  <span className="flex items-center justify-end gap-1">Buy (from) <ArrowUpDown className="w-3 h-3" /></span>
                </th>
                <th className="w-[160px] px-3 py-3 text-left">Rent (from)</th>
                <th className="w-[90px] px-3 py-3 text-center">Publish</th>
                <th className="w-[70px] px-3 py-3 text-center">Actions</th>
              </>)}
              {colGroups.baseline && (<>
                <th className="w-[120px] px-3 py-3 text-right bg-amber-50/50">Hours @ Rated</th>
                <th className="w-[160px] px-3 py-3 text-right bg-amber-50/50">Base Units (1c/d)</th>
                <th className="w-[160px] px-3 py-3 text-right bg-amber-50/50">Min Billable (70%)</th>
              </>)}
              {colGroups.website && (<>
                <th className="w-[90px] px-3 py-3 text-center bg-blue-50/50">Show Buy</th>
                <th className="w-[90px] px-3 py-3 text-center bg-blue-50/50">Show Rent</th>
                <th className="w-[90px] px-3 py-3 text-center bg-blue-50/50">Featured</th>
                <th className="w-[120px] px-3 py-3 text-center bg-blue-50/50">Order</th>
              </>)}
              {colGroups.audit && (<>
                <th className="w-[160px] px-3 py-3 text-left bg-slate-100/50">Last Updated</th>
                <th className="w-[140px] px-3 py-3 text-left bg-slate-100/50">Compat</th>
              </>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.map(r => (
              <SheetRow key={r.sheet_row_id} row={r} colGroups={colGroups}
                selected={selected.has(r.sheet_row_id)}
                onToggle={() => toggleOne(r.sheet_row_id)}
                onOpenDrawer={() => setDrawerRow(r)}
                onRefresh={fetchData} />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={20} className="text-center py-12 text-sm text-slate-400">No rows. Click "Sync from Cost Matrix" to populate.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {drawerRow && <SKUDrawer row={drawerRow} onClose={() => setDrawerRow(null)} onRefresh={() => { fetchData(); setDrawerRow(null); }} />}
    </div>
  );
}

/* ==================== SHEET ROW ==================== */
function SheetRow({ row: r, colGroups, selected, onToggle, onOpenDrawer, onRefresh }) {
  const [editMargin, setEditMargin] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const saveMargin = async () => {
    const val = parseFloat(editMargin) / 100;
    if (isNaN(val)) return;
    try {
      await catalogAPI.updateSheetRow(r.sheet_row_id, { margin_pct: val });
      setEditMargin(null);
      onRefresh();
    } catch { toast.error('Failed to save margin'); }
  };

  const handleRecompute = async (e) => {
    e.stopPropagation();
    setRecomputing(true);
    try {
      await catalogAPI.recomputeRent(r.sheet_row_id);
      toast.success(`Rent computed for ${r.voltide_sku_code}`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRecomputing(false); }
  };

  const handleTogglePublish = async (checked) => {
    try {
      await catalogAPI.updateSheetRow(r.sheet_row_id, { publish_to_website: checked });
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleToggleField = async (field, checked) => {
    try {
      await catalogAPI.updateSheetRow(r.sheet_row_id, { [field]: checked });
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const compatColors = {
    YES: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    POSSIBLE: 'bg-amber-50 text-amber-700 border-amber-200',
    ENGINEERING_REVIEW: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  return (
    <tr className="hover:bg-slate-50 cursor-pointer h-[72px]" onClick={onOpenDrawer} data-testid={`sheet-row-${r.sheet_row_id}`}>
      <td className="px-2 py-2 sticky left-0 bg-white z-10" onClick={e => e.stopPropagation()}>
        <Checkbox checked={selected} onCheckedChange={onToggle} />
      </td>
      {/* Voltide SKU */}
      <td className="px-3 py-2 sticky left-[40px] bg-white z-10">
        <span className="font-mono text-xs font-semibold text-slate-900">{r.voltide_sku_code}</span>
      </td>
      {/* Vendor SKU (internal) */}
      <td className="px-3 py-2 sticky left-[240px] bg-white z-10">
        <span className="font-mono text-[10px] text-slate-500">{r.vendor_sku_code || '—'}</span>
      </td>
      {/* Name / Config */}
      <td className="px-3 py-2 sticky left-[420px] bg-white z-10">
        <div>
          <span className="text-xs font-medium text-slate-900">{r.public_product_name}</span>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[10px] text-slate-400">{r.rated_power_kw}kW / {r.battery_energy_kwh}kWh</span>
            <Badge className="text-[8px] px-1 py-0 h-3.5 bg-slate-100 text-slate-500 border-slate-200">{r.sku_type}</Badge>
            <Badge className="text-[8px] px-1 py-0 h-3.5 bg-slate-50 text-slate-400 border-slate-200">{r.install_complexity}</Badge>
          </div>
        </div>
      </td>

      {/* PRIMARY COLUMNS */}
      {colGroups.primary && (<>
        {/* Total Cost */}
        <td className="px-3 py-2 text-right">
          {r.total_bess_cost > 0 ? (
            <span className="font-mono text-xs text-slate-700">{fmtCompact(r.total_bess_cost)}</span>
          ) : <span className="text-xs text-slate-300">—</span>}
        </td>
        {/* Margin % */}
        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
          {editMargin !== null ? (
            <div className="flex items-center gap-0.5">
              <Input type="number" className="h-7 text-xs w-14 text-center" value={editMargin}
                onChange={e => setEditMargin(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveMargin(); if (e.key === 'Escape') setEditMargin(null); }}
                autoFocus data-testid={`margin-input-${r.sheet_row_id}`} />
              <span className="text-[10px] text-slate-400">%</span>
            </div>
          ) : (
            <button onClick={() => setEditMargin(((r.margin_pct || 0) * 100).toFixed(0))}
              className="font-mono text-xs text-slate-700 hover:text-blue-600 hover:underline cursor-pointer"
              title="Click to edit margin" data-testid={`margin-${r.sheet_row_id}`}>
              {((r.margin_pct || 0) * 100).toFixed(0)}%
            </button>
          )}
        </td>
        {/* Buy (from) */}
        <td className="px-3 py-2 text-right">
          {r.buy_from_price_ex_gst > 0 ? (
            <div>
              <span className="font-mono text-xs font-semibold text-slate-900">{fmtCompact(r.buy_from_price_ex_gst)}</span>
              {r.buy_price_override && <span className="text-[8px] text-blue-500 ml-0.5">OVR</span>}
              <p className="text-[10px] text-slate-400">ex-GST</p>
            </div>
          ) : <span className="text-xs text-slate-300">—</span>}
        </td>
        {/* Rent (from) */}
        <td className="px-3 py-2">
          {r.rent_from_inr_per_kwh_month ? (
            <div>
              <span className="font-mono text-xs font-semibold text-slate-900">₹{Number(r.rent_from_inr_per_kwh_month).toFixed(2)}</span>
              <span className="text-[10px] text-slate-400 ml-0.5">/kWh/mo</span>
              <p className="text-[9px] text-slate-400 mt-0.5">Hours @ rated: {r.hours_at_rated_power ?? '—'} h</p>
              <p className="text-[9px] text-slate-400">Base: {r.baseline_monthly_units_kwh?.toLocaleString()} kWh/mo</p>
              <p className="text-[9px] text-slate-400">Min (70%): {r.min_billable_units_kwh?.toLocaleString()} kWh/mo</p>
            </div>
          ) : (
            <div>
              <span className="text-xs text-slate-300">—</span>
              <p className="text-[10px] text-amber-500">Recompute</p>
            </div>
          )}
        </td>
        {/* Publish */}
        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex flex-col items-center gap-0.5">
            <Switch checked={r.publish_to_website} onCheckedChange={handleTogglePublish} data-testid={`pub-${r.sheet_row_id}`} />
            <span className={`text-[9px] ${r.publish_to_website ? 'text-emerald-600' : 'text-slate-400'}`}>
              {r.publish_to_website ? 'Live' : 'Off'}
            </span>
          </div>
        </td>
        {/* Actions */}
        <td className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center gap-0.5">
            <button onClick={handleRecompute} disabled={recomputing} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Recompute rent">
              <RefreshCw className={`w-3.5 h-3.5 ${recomputing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onOpenDrawer(); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" title="Open details">
              <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
            </button>
          </div>
        </td>
      </>)}

      {/* BASELINE COLUMNS */}
      {colGroups.baseline && (<>
        <td className="px-3 py-2 text-right bg-amber-50/20">
          <span className="font-mono text-xs text-slate-700">{r.hours_at_rated_power ?? '—'} h</span>
        </td>
        <td className="px-3 py-2 text-right bg-amber-50/20">
          <span className="font-mono text-xs text-slate-700">{r.baseline_monthly_units_kwh?.toLocaleString()} kWh</span>
        </td>
        <td className="px-3 py-2 text-right bg-amber-50/20">
          <span className="font-mono text-xs text-slate-700">{r.min_billable_units_kwh?.toLocaleString()} kWh</span>
        </td>
      </>)}

      {/* WEBSITE CONTROL COLUMNS */}
      {colGroups.website && (<>
        <td className="px-3 py-2 text-center bg-blue-50/10" onClick={e => e.stopPropagation()}>
          <Switch checked={r.show_buy_price} onCheckedChange={v => handleToggleField('show_buy_price', v)} />
        </td>
        <td className="px-3 py-2 text-center bg-blue-50/10" onClick={e => e.stopPropagation()}>
          <Switch checked={r.show_rent_price} onCheckedChange={v => handleToggleField('show_rent_price', v)} />
        </td>
        <td className="px-3 py-2 text-center bg-blue-50/10" onClick={e => e.stopPropagation()}>
          <Switch checked={r.featured} onCheckedChange={v => handleToggleField('featured', v)} />
        </td>
        <td className="px-3 py-2 text-center bg-blue-50/10 font-mono text-xs text-slate-500">
          {r.display_order}
        </td>
      </>)}

      {/* AUDIT COLUMNS */}
      {colGroups.audit && (<>
        <td className="px-3 py-2 text-[10px] text-slate-500 bg-slate-50/30">
          {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}
        </td>
        <td className="px-3 py-2 bg-slate-50/30">
          <Badge className={`text-[8px] px-1.5 py-0 ${compatColors[r.compatibility_status] || 'bg-slate-100 text-slate-500'}`}>
            {r.compatibility_status || '—'}
          </Badge>
        </td>
      </>)}
    </tr>
  );
}

/* ==================== DRAWER ==================== */
function SKUDrawer({ row, onClose, onRefresh }) {
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto p-0" data-testid="sku-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <SheetTitle className="text-base">{row.public_product_name}</SheetTitle>
          <p className="text-xs text-slate-500 font-mono">{row.voltide_sku_code}</p>
          <p className="text-[10px] text-slate-400">Vendor: {row.vendor_sku_code} (internal)</p>
        </SheetHeader>
        <Tabs defaultValue="pricing" className="px-5 pt-3 pb-5">
          <TabsList className="w-full grid grid-cols-4 h-8">
            <TabsTrigger value="pricing" className="text-xs">Pricing</TabsTrigger>
            <TabsTrigger value="publish" className="text-xs">Publish</TabsTrigger>
            <TabsTrigger value="details" className="text-xs">Details</TabsTrigger>
            <TabsTrigger value="audit" className="text-xs">Audit</TabsTrigger>
          </TabsList>
          <TabsContent value="pricing"><DrawerPricing row={row} onRefresh={onRefresh} /></TabsContent>
          <TabsContent value="publish"><DrawerPublish row={row} onRefresh={onRefresh} /></TabsContent>
          <TabsContent value="details"><DrawerDetails row={row} /></TabsContent>
          <TabsContent value="audit"><DrawerAudit row={row} /></TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function DrawerPricing({ row: r, onRefresh }) {
  const [recomputing, setRecomputing] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await catalogAPI.recomputeRent(r.sheet_row_id);
      toast.success('Recomputed');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRecomputing(false); }
  };

  return (
    <div className="space-y-4 mt-3">
      {/* Cost + Buy */}
      <div className="bg-slate-50 rounded-sm p-4 space-y-2">
        <p className="text-xs font-semibold text-slate-700 uppercase">Buy Pricing</p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Kv label="Total Cost" value={fmt(r.total_bess_cost) || '—'} />
          <Kv label="Margin" value={`${((r.margin_pct || 0) * 100).toFixed(0)}%`} />
          <Kv label="Buy (from)" value={fmt(r.buy_from_price_ex_gst) || '—'} highlight />
        </div>
        {r.buy_price_override && <p className="text-[10px] text-blue-500">Manual override active</p>}
      </div>

      {/* Rent */}
      <div className="bg-slate-50 rounded-sm p-4 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-700 uppercase">Rent Pricing</p>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={handleRecompute} disabled={recomputing}>
            <RefreshCw className={`w-3 h-3 mr-1 ${recomputing ? 'animate-spin' : ''}`} /> Recompute
          </Button>
        </div>
        {r.rent_from_inr_per_kwh_month ? (
          <>
            <p className="font-mono text-lg font-bold text-slate-900">
              ₹{Number(r.rent_from_inr_per_kwh_month).toFixed(2)} <span className="text-xs font-normal text-slate-400">/ kWh / mo</span>
            </p>
            <p className="text-[10px] text-slate-400">Computed: {r.rent_computed_at ? new Date(r.rent_computed_at).toLocaleString() : '—'}</p>
          </>
        ) : <p className="text-sm text-amber-600">Not computed yet.</p>}

        {/* Baseline */}
        <div className="mt-2 pt-2 border-t border-slate-200">
          <p className="text-[10px] font-semibold text-slate-600 uppercase mb-1">Internal Baseline (1 cycle/day)</p>
          <div className="grid grid-cols-3 gap-2">
            <Kv label="Hours @ Rated" value={`${r.hours_at_rated_power ?? '—'} h`} />
            <Kv label="Base Units" value={`${r.baseline_monthly_units_kwh?.toLocaleString()} kWh/mo`} />
            <Kv label="Min Billable (70%)" value={`${r.min_billable_units_kwh?.toLocaleString()} kWh/mo`} />
          </div>
          {r.rent_from_inr_per_kwh_month && r.min_billable_units_kwh > 0 && (
            <Kv label="Min Monthly Charge (internal)" value={fmt(r.min_billable_units_kwh * r.rent_from_inr_per_kwh_month)} highlight />
          )}
        </div>

        {r.rent_from_inr_per_kwh_month && (
          <>
            <button onClick={() => setShowAssumptions(!showAssumptions)} className="text-[10px] text-blue-600 hover:underline">
              {showAssumptions ? 'Hide' : 'Show'} Assumptions
            </button>
            {showAssumptions && r.rent_inputs_json && (
              <div className="bg-white rounded p-2 text-[10px] space-y-0.5 border border-slate-100">
                <p><strong>Term:</strong> {r.rent_inputs_json?.levers?.contract_term_months}mo</p>
                <p><strong>IRR:</strong> {((r.rent_inputs_json?.levers?.target_irr_annual || 0) * 100).toFixed(1)}%</p>
                <p><strong>Buffer:</strong> {((r.rent_inputs_json?.levers?.negotiation_buffer_pct || 0) * 100).toFixed(1)}%</p>
                <p><strong>Deposit:</strong> {r.rent_inputs_json?.levers?.refundable_deposit_months}mo</p>
                <p><strong>Asset Cost:</strong> {fmt(r.rent_inputs_json?.total_bess_cost)}</p>
              </div>
            )}
          </>
        )}
      </div>

      {r.disclaimer_text && (
        <div className="bg-amber-50 rounded-sm p-3 border border-amber-100">
          <p className="text-xs text-amber-700"><strong>Disclaimer:</strong> {r.disclaimer_text}</p>
        </div>
      )}
    </div>
  );
}

function DrawerPublish({ row: r, onRefresh }) {
  const [form, setForm] = useState({
    publish_to_website: r.publish_to_website,
    show_buy_price: r.show_buy_price ?? true,
    show_rent_price: r.show_rent_price ?? true,
    featured: r.featured || false,
    display_order: r.display_order || 0,
    install_complexity: r.install_complexity || 'STANDARD',
    checkout_eligible: r.checkout_eligible || false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await catalogAPI.updateSheetRow(r.sheet_row_id, form);
      toast.success('Saved');
      onRefresh();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 mt-3">
      <div className="space-y-3 bg-slate-50 rounded-sm p-4">
        {[
          ['publish_to_website', 'Publish to Website'],
          ['show_buy_price', 'Show Buy Price'],
          ['show_rent_price', 'Show Rent Price'],
          ['featured', 'Featured'],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between">
            <Label className="text-xs">{label}</Label>
            <Switch checked={form[k]} onCheckedChange={v => set(k, v)} />
          </div>
        ))}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">Checkout Eligible</Label>
            {form.install_complexity !== 'SIMPLE' && <p className="text-[10px] text-amber-500">SIMPLE only</p>}
          </div>
          <Switch checked={form.checkout_eligible} onCheckedChange={v => set('checkout_eligible', v)} disabled={form.install_complexity !== 'SIMPLE'} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Display Order</Label>
          <Input type="number" value={form.display_order} onChange={e => set('display_order', parseInt(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-xs">Install Complexity</Label>
          <Select value={form.install_complexity} onValueChange={v => { set('install_complexity', v); if (v !== 'SIMPLE') set('checkout_eligible', false); }}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="SIMPLE">Simple</SelectItem>
              <SelectItem value="STANDARD">Standard</SelectItem>
              <SelectItem value="CUSTOM">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button className="w-full bg-slate-900" onClick={handleSave} disabled={saving} data-testid="save-publish-btn">
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );
}

function DrawerDetails({ row: r }) {
  return (
    <div className="space-y-4 mt-3">
      <div className="bg-slate-50 rounded-sm p-4">
        <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Specifications</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <Kv label="Voltide SKU" value={r.voltide_sku_code} />
          <Kv label="Public Slug" value={r.public_slug} />
          <Kv label="Power" value={`${r.rated_power_kw} kW`} />
          <Kv label="Energy" value={`${r.battery_energy_kwh} kWh`} />
          <Kv label="PCS" value={r.pcs_sku} />
          <Kv label="Battery" value={r.battery_sku} />
          <Kv label="Container" value={r.container_or_enclosure || '—'} />
          <Kv label="Compatibility" value={r.compatibility_status || '—'} />
        </div>
      </div>
      <div className="bg-slate-50 rounded-sm p-4">
        <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Vendor Mapping (Internal)</p>
        <Kv label="Vendor" value={r.vendor_name || r.vendor_id || '—'} />
        <Kv label="Vendor SKU" value={r.vendor_sku_code || '—'} />
      </div>
      <div className="bg-slate-50 rounded-sm p-4">
        <p className="text-xs font-semibold text-slate-700 uppercase mb-2">Cost Matrix</p>
        <Kv label="Matrix Row ID" value={r.cost_matrix_row_id || '—'} />
        <Kv label="Total BESS Cost" value={fmt(r.total_bess_cost) || '—'} />
      </div>
    </div>
  );
}

function DrawerAudit({ row: r }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await catalogAPI.getSheetAudit(r.sheet_row_id); setLogs(res.data); }
      catch (e) { console.error('getSheetAudit failed', e); }
      finally { setLoading(false); }
    })();
  }, [r.sheet_row_id]);

  const handleExport = (format) => {
    const data = format === 'json' ? JSON.stringify(logs, null, 2)
      : ['timestamp,action,resource_type,actor_email', ...logs.map(l => `${l.timestamp},${l.action},${l.resource_type},${l.actor_email}`)].join('\n');
    const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit_${r.voltide_sku_code}.${format}`;
    a.click();
  };

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-700">{logs.length} event(s)</p>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => handleExport('csv')}><Download className="w-3 h-3 mr-1" />CSV</Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => handleExport('json')}><Download className="w-3 h-3 mr-1" />JSON</Button>
        </div>
      </div>
      {loading ? <p className="text-xs text-slate-400 py-4 text-center">Loading...</p>
        : logs.length === 0 ? <p className="text-xs text-slate-400 py-4 text-center">No audit logs.</p>
        : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {logs.map((l, i) => (
              <div key={`${l.timestamp || i}-${l.action || ''}`} className="bg-slate-50 rounded p-2 border border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-900">{l.action}</span>
                  <span className="text-[10px] text-slate-400">{l.timestamp ? new Date(l.timestamp).toLocaleString() : '—'}</span>
                </div>
                <p className="text-[10px] text-slate-500">{l.resource_type} &middot; {l.actor_email}</p>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function Kv({ label, value, highlight }) {
  return (
    <div className="mb-1">
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className={`text-xs font-medium ${highlight ? 'text-emerald-700' : 'text-slate-800'}`}>{value || '—'}</p>
    </div>
  );
}
